/**
 * RecommendationsTab — the "Discover" tab in DeckZone.
 *
 * Section 1: Tags to consider — ExploreDirectionsSection tag suggestions.
 * Section 2: Recommendation buckets from recommander.cards. Each chip is a
 *   bar-writer: clicking it toggles a `rectag:<slug>` clause in the search
 *   bar (mirrors the otag: pattern), and useSearch expands that clause to
 *   oracleid: groups against the cached deck recommendations. The cards
 *   themselves render in the main search results panel — this tab is just
 *   the chip row + counts. We deliberately skip per-card-type buckets.
 */

import React, { useMemo } from 'react';
import {
  RECOMMANDER_BUCKETS,
  RECOMMANDER_BUCKET_LABELS,
  type RecommanderBucket,
} from '@mtg-deck-tool/shared';
import { useDeckStore } from '../../stores/deck-store.js';
import { useSearchStore } from '../../stores/search-store.js';
import { ExploreDirectionsSection } from './ExploreDirectionsSection.js';
import type { RecommanderData } from '../../hooks/useRecommendations.js';

// ─── BucketChip ──────────────────────────────────────────────────────────────

interface BucketChipProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

function BucketChip({ label, count, active, onClick }: BucketChipProps) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors whitespace-nowrap ${
        active
          ? 'bg-amber-500/20 border-amber-500/60 text-amber-200'
          : 'bg-gray-800/40 border-gray-700/60 text-gray-400 hover:bg-gray-800 hover:text-gray-200'
      }`}
    >
      {label}
      <span className="ml-1 text-gray-500 tabular-nums">{count}</span>
    </button>
  );
}

// ─── RecommendationsTab ──────────────────────────────────────────────────────

export function RecommendationsTab() {
  const moxfieldDeckId = useDeckStore((s) => s.moxfieldDeckId);
  const recommendationsCache = useDeckStore((s) => s.recommendationsCache);
  const sideboard = useDeckStore((s) => s.sideboard);
  const isLoading = useDeckStore((s) => s.isImportLoading);
  const activeRecBucket = useSearchStore((s) => s.activeRecBucket);
  const toggleRecBucket = useSearchStore((s) => s.toggleRecBucket);
  const error: string | null = null; // errors handled by the DeckZone-level hook

  const data = moxfieldDeckId
    ? ((recommendationsCache.get(moxfieldDeckId) as RecommanderData | undefined) ?? null)
    : null;

  // Hide cards already in sideboard from the chip counts — user has already considered them.
  const sideboardOracleIds = useMemo(() => new Set(sideboard.map((s) => s.card.oracleId)), [sideboard]);

  const bucketCounts = useMemo(() => {
    if (!data) return null;
    return Object.fromEntries(
      RECOMMANDER_BUCKETS.map((b) => {
        const cards = b === 'Top'
          ? data.recommendations.filter((r) => !r.generic)
          : (data.typed_recommendations[b] ?? []);
        return [b, cards.filter((r) => !sideboardOracleIds.has(r.oracle_id)).length];
      }),
    ) as Record<RecommanderBucket, number>;
  }, [data, sideboardOracleIds]);

  return (
    <div className="flex flex-col gap-0">
      {/* ── Section 1: Tags to consider ─────────────────────────── */}
      <div className="px-4 py-3 border-b border-gray-800">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Tags to consider
        </h3>
        <ExploreDirectionsSection />
      </div>

      {/* ── Section 2: Recommendation buckets ────────────────────────── */}
      <div className="px-4 py-3">
        {!moxfieldDeckId && (
          <p className="text-xs text-gray-500">
            Import a deck from Moxfield to see recommendations.
          </p>
        )}

        {moxfieldDeckId && isLoading && (
          <div className="flex items-center gap-2 py-2">
            <div className="w-3 h-3 border-2 border-orange-500 border-t-transparent rounded-full animate-spin shrink-0" />
            <p className="text-xs text-gray-400">Loading recommendations…</p>
          </div>
        )}

        {moxfieldDeckId && !isLoading && error && (
          <p className="text-xs text-red-400">{error}</p>
        )}

        {moxfieldDeckId && !isLoading && !error && data && bucketCounts && (
          <>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Recommendations
            </h3>
            <p className="text-[11px] text-gray-500 mb-2">
              Click a bucket to show those recommendations in search.
            </p>
            <div className="flex flex-wrap gap-1">
              {RECOMMANDER_BUCKETS.map((b) => (
                <BucketChip
                  key={b}
                  label={RECOMMANDER_BUCKET_LABELS[b]}
                  count={bucketCounts[b]}
                  active={activeRecBucket === b}
                  onClick={() => toggleRecBucket(b)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
